import type { EditorView } from '@atlaskit/editor-prosemirror/view';

import { pluginKey } from '../../common/agentManagedExtensionPluginType';
import { findAgentManagedExtensionByLocalId } from '../../common/utils';
import { setMeta } from './state';

interface AutoOpenView {
	destroy: () => void;
	update: () => void;
}

const MAX_AUTO_OPEN_ATTEMPTS = 5;

const getPendingAutoOpenLocalId = (editorView: EditorView): string | undefined =>
	pluginKey.getState(editorView.state)?.pendingAutoOpenLocalId;

const hasRenderedBlockDom = (editorView: EditorView, localId: string): boolean => {
	const found = findAgentManagedExtensionByLocalId(editorView.state, localId);
	return (
		found?.node.attrs.localId === localId && editorView.nodeDOM(found.pos) instanceof HTMLElement
	);
};

const openConfigForBlock = (editorView: EditorView, localId: string): void => {
	editorView.dispatch(setMeta(editorView.state.tr, { localId, type: 'OPEN_CONFIG' }));
};

/**
 * Watches for newly-inserted agent-managed extensions and dispatches
 * targeted `OPEN_CONFIG` when the inserted block appears in the doc. Only
 * triggers for genuine user insertions — not on initial document load,
 * document replacement, or remote/collab operations.
 *
 * Returns the `view` object expected by the ProseMirror plugin spec.
 */
export const createAutoOpenConfigOnInsertView = (editorView: EditorView): AutoOpenView => {
	const handledLocalIds = new Set<string>();
	let scheduledLocalId: string | undefined;
	let animationFrameId: number | undefined;
	let remainingAttempts = 0;

	const clearScheduledOpen = () => {
		if (animationFrameId !== undefined) {
			cancelAnimationFrame(animationFrameId);
			animationFrameId = undefined;
		}
		scheduledLocalId = undefined;
		remainingAttempts = 0;
	};

	const getActiveScheduledLocalId = (): string | undefined => {
		if (!scheduledLocalId || handledLocalIds.has(scheduledLocalId)) {
			return undefined;
		}
		const pendingLocalId = getPendingAutoOpenLocalId(editorView);
		return pendingLocalId === scheduledLocalId ? scheduledLocalId : undefined;
	};

	const attemptOpenConfig = () => {
		animationFrameId = undefined;

		const localId = getActiveScheduledLocalId();
		if (!localId) {
			clearScheduledOpen();
			return;
		}

		if (hasRenderedBlockDom(editorView, localId)) {
			handledLocalIds.add(localId);
			openConfigForBlock(editorView, localId);
			clearScheduledOpen();
			return;
		}

		remainingAttempts -= 1;
		if (remainingAttempts <= 0) {
			clearScheduledOpen();
			return;
		}

		animationFrameId = requestAnimationFrame(attemptOpenConfig);
	};

	const scheduleOpenConfig = (pendingLocalId: string) => {
		if (scheduledLocalId === pendingLocalId && animationFrameId !== undefined) {
			return;
		}
		scheduledLocalId = pendingLocalId;
		remainingAttempts = MAX_AUTO_OPEN_ATTEMPTS;
		animationFrameId = requestAnimationFrame(attemptOpenConfig);
	};

	return {
		destroy() {
			clearScheduledOpen();
		},
		update() {
			const pendingLocalId = getPendingAutoOpenLocalId(editorView);
			if (!pendingLocalId || handledLocalIds.has(pendingLocalId)) {
				return;
			}
			scheduleOpenConfig(pendingLocalId);
		},
	};
};
